import clsx from 'clsx'
import { useMemo, useState, type FC } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Pagination } from '@wartek-id/pagination'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'
import { Link } from '@wartek-id/link'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import {
  useAPIGetPelaksanaanConfig,
  useAPIGetPredikatKinerjaOrganisasi,
} from 'api/kinerja/useAPIKinerja'
import { DialogUbahPredikatOrganisasi } from 'app/KinerjaDinasPemberiPredikatPage/components/DialogUbahPredikatOrganisasi'
import FilterSendState from 'app/KinerjaDinasPemberiPredikatPage/components/FilterSendState'
import OverviewPredikatOrganisasi from 'app/KinerjaDinasPemberiPredikatPage/components/OverviewPredikatOrganisasi'
import { ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'
import CardDeadlinePeriod from 'app/KinerjaShared/components/CardDeadlinePeriod'
import { Header } from 'app/KinerjaShared/components/Header'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { PENETAPAN_PREDIKAT_KINERJA_STEPS } from 'app/KinerjaShared/constants/penetapanPredikatKinerja'
import { formatDate, isDateValid } from 'utils/datetime'
import { PREDIKAT_ORGANISASI_STATUS_LABELS } from './constants/predikatOrganisasiStatus'
import { assetUrl } from 'utils/url'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { PANDUAN_PENGELOLAAN_GURU_PENETAPAN_PKO_URL } from 'app/KinerjaShared/utils/panduan'

import type {
  IPredikatKinerjaOrganisasi,
  TPredicateRecommendationResult,
  TPredikatKinerjaSubmissionStep,
  TPredikatOrganisasiStatus,
  TPredikatOrganisasiStatusFilter,
} from 'types/kinerja-pelaksanaan'

interface KinerjaDinasPemberiPredikatKinerjaOrganisasiKirimProps {}

const KinerjaDinasPemberiPredikatKinerjaOrganisasiKirim: FC<
  KinerjaDinasPemberiPredikatKinerjaOrganisasiKirimProps
> = ({}) => {
  const isPemberiPredikatPageEnabled =
    process.env.NEXT_PUBLIC_SKP_KADIN_PEMBERI_PREDIKAT_ENABLED === 'true'

  const CURRENT_LIMIT = 20

  const [statusKinerja, setStatusKinerja] =
    useState<TPredikatOrganisasiStatusFilter>('ALL')
  const [currentPage, setCurrentPage] = useState<number>(1)

  const {
    data: dataPredikatKinerjaOrganisasi,
    isLoading: isLoadingPredikatKinerjaOrganisasi,
    isSuccess: isSuccessPredikatKinerjaOrganisasi,
  } = useAPIGetPredikatKinerjaOrganisasi(
    {
      status: statusKinerja,
      limit: CURRENT_LIMIT,
      offset: currentPage <= 1 ? 0 : (currentPage - 1) * CURRENT_LIMIT,
    },
    {
      staleTime: 30 * 60 * 1000, // stale after 30 min, force to refetch upon mounting
    }
  )

  const handleFilterChange = (value: TPredikatOrganisasiStatusFilter) => {
    setCurrentPage(0)
    setStatusKinerja(value)
  }

  const { data: dataPelaksanaanConfig } = useAPIGetPelaksanaanConfig({
    params: { key: 'PENETAPAN_PREDIKAT_KINERJA' },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      enabled: isPemberiPredikatPageEnabled,
    },
  })

  // deadline date
  const deadlineDate =
    dataPelaksanaanConfig?.data?.predicatePerformanceDeadlineDate
  const isValidDeadlineDate = isDateValid(deadlineDate)
  const formattedDeadlineDate = isValidDeadlineDate
    ? formatDate(deadlineDate)
    : '-'

  const renderShimmer = () =>
    Array(3)
      .fill('')
      .map((_x, i) => (
        <TableRow key={i}>
          <TableRowItem className="w-4/12">
            <Shimmer className="!w-full" />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
          <TableRowItem>
            <Shimmer />
          </TableRowItem>
        </TableRow>
      ))

  const renderBlueCircle = () => (
    <Icon
      className="!w-2 !h-2 mr-1"
      color="informational"
      style={{ fontSize: '8px' }}
    >
      circle
    </Icon>
  )

  const filterStates: Record<TPredikatOrganisasiStatusFilter, number> =
    useMemo(() => {
      if (!isSuccessPredikatKinerjaOrganisasi)
        return {
          ALL: 0,
          NOT_SENT: 0,
          READY_TO_SEND: 0,
          SENT: 0,
        }
      const meta = dataPredikatKinerjaOrganisasi?.meta
      return {
        ALL: meta?.total || 0,
        NOT_SENT: meta?.deliveryStatus?.NOT_SENT || 0,
        READY_TO_SEND: meta?.deliveryStatus?.READY_TO_SEND || 0,
        SENT: meta?.deliveryStatus?.SENT || 0,
      }
    }, [dataPredikatKinerjaOrganisasi, isSuccessPredikatKinerjaOrganisasi])

  const predikatOrganisasiData: {
    readyToSendIds: string[]
    predicateResult: Record<TPredicateRecommendationResult, number>
    isAllSent: boolean
  } = useMemo(() => {
    if (!isSuccessPredikatKinerjaOrganisasi)
      return {
        readyToSendIds: [],
        predicateResult: {
          VERY_GOOD: 0,
          GOOD: 0,
          NEED_IMPROVEMENT: 0,
          BAD: 0,
          VERY_BAD: 0,
        },
        isAllSent: false,
      }

    const meta = dataPredikatKinerjaOrganisasi?.meta

    return {
      readyToSendIds: dataPredikatKinerjaOrganisasi?.meta?.readyToSendIds ?? [],
      predicateResult: {
        VERY_GOOD: meta?.predicateRecommendation?.VERY_GOOD || 0,
        GOOD: meta?.predicateRecommendation?.GOOD || 0,
        NEED_IMPROVEMENT: meta?.predicateRecommendation?.NEED_IMPROVEMENT || 0,
        BAD: meta?.predicateRecommendation?.BAD || 0,
        VERY_BAD: meta?.predicateRecommendation?.VERY_BAD || 0,
      },
      isAllSent: meta?.deliveryStatus?.['SENT'] === meta?.total,
    }
  }, [dataPredikatKinerjaOrganisasi, isSuccessPredikatKinerjaOrganisasi])

  return (
    <div className="px-8 pb-8 pt-12 bg-gray-10 min-h-full">
      <div className="max-w-7xl mx-auto space-y-9">
        <Header
          title="Kirim Predikat Kinerja Organisasi"
          rightNode={
            <section className="bg-white rounded">
              <Button
                variant="ghost"
                className="flex gap-1.5 !py-3"
                as="a"
                type={undefined}
                target="_blank"
                href={PANDUAN_PENGELOLAAN_GURU_PENETAPAN_PKO_URL}
              >
                <Icon fontSize="sm" color="informational">
                  help
                </Icon>
                Panduan Predikat Kinerja Organisasi
              </Button>
            </section>
          }
        />

        <section className="p-6 space-y-3 bg-white rounded border border-subdued">
          <Text className="!text-black" variant="heading-sm" as="h2">
            Langkah-langkah Pengiriman
          </Text>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Text className="!text-blue-60" variant="action">
                Cek data satuan pendidikan
              </Text>
              <Text>
                Jika belum ada, artinya Kepala Sekolah masih di tahap penilaian.
              </Text>
            </div>

            <div className="flex space-x-3">
              <Icon color="informational">arrow_circle_right</Icon>
              <div>
                <Text className="!text-blue-60" variant="action">
                  Cek rekomendasi Predikat Kinerja Organisasi
                </Text>
                <Text>Anda dapat ubah jika perlu.</Text>
              </div>
            </div>

            <div className="flex space-x-3">
              <Icon color="informational">arrow_circle_right</Icon>
              <div>
                <Text className="!text-blue-60" variant="action">
                  Kirim ke Kepala Sekolah
                </Text>
                <Text>
                  Kepala Sekolah akan mendapatkan Predikat Kinerja Organisasi
                  sesuai dengan yang Anda kirim.
                </Text>
              </div>
            </div>
          </div>
        </section>
        <section className="rounded-lg border border-subdued bg-surface-default space-y-6">
          <div className="space-y-3 px-6 pt-6">
            <Text variant="heading-md">Daftar Predikat Kinerja Organisasi</Text>
            <Text variant="eyebrow-lg" color="subdued">
              Data mungkin muncul secara berkala. Jika sudah dikirim, data tidak
              dapat diubah lagi.
            </Text>
            <CardDeadlinePeriod deadlineDate={formattedDeadlineDate} />
          </div>

          {predikatOrganisasiData.isAllSent ? (
            <div className="px-6">
              <div className="inline-flex px-4 py-3 space-x-4 rounded border border-green-50 bg-green-5 items-center">
                <Icon
                  fontSize="lg"
                  className="!text-4xl !leading-none"
                  color="success"
                >
                  check_circle_outlined
                </Icon>
                <div className="space-y-1">
                  <Text variant="heading-sm" className="!text-green-50">
                    Semua Predikat Kinerja Organisasi sudah dikirim
                  </Text>
                  <Text variant="body-sm" color="subdued">
                    Terima kasih. Cek data di bawah jika perlu.
                  </Text>
                </div>
              </div>
            </div>
          ) : (
            <OverviewPredikatOrganisasi
              isLoading={isLoadingPredikatKinerjaOrganisasi}
              separateAfter={3}
              ids={predikatOrganisasiData.readyToSendIds}
              predicateResult={predikatOrganisasiData.predicateResult}
            />
          )}
          <FilterSendState
            filterValue={filterStates}
            activeFilter={statusKinerja}
            onChange={(value) => handleFilterChange(value)}
            isLoading={isLoadingPredikatKinerjaOrganisasi}
          />
          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>Nama Satuan Pendidikan</TableHeaderItem>
              <TableHeaderItem>
                Rekomendasi Predikat
                <br />
                Kinerja Organisasi
              </TableHeaderItem>
              <TableHeaderItem>
                Perubahan Predikat
                <br />
                Kinerja Organisasi
              </TableHeaderItem>
              <TableHeaderItem className="w-56">
                Status Pengiriman
              </TableHeaderItem>
              <TableHeaderItem />
            </TableHeader>
            <TableBody>
              {isLoadingPredikatKinerjaOrganisasi && renderShimmer()}
              {!isLoadingPredikatKinerjaOrganisasi &&
                dataPredikatKinerjaOrganisasi?.data?.map(
                  (thisPredikatKinerjaOrganisasi) => (
                    <TableRow key={thisPredikatKinerjaOrganisasi.schoolName}>
                      <TableRowItem className="overflow-wrap-anywhere">
                        {thisPredikatKinerjaOrganisasi.schoolName}
                      </TableRowItem>
                      <TableRowItem>
                        <Text>
                          {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[
                            thisPredikatKinerjaOrganisasi
                              .predicateRecommendation
                          ] || '-'}
                        </Text>
                      </TableRowItem>
                      <TableRowItem>
                        {thisPredikatKinerjaOrganisasi.predicateRecommendationChange &&
                          renderBlueCircle()}
                        {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[
                          thisPredikatKinerjaOrganisasi
                            .predicateRecommendationChange
                        ] || '-'}
                      </TableRowItem>
                      <TableRowItem>
                        <DeliveryStatus
                          value={thisPredikatKinerjaOrganisasi.deliveryStatus}
                        />
                      </TableRowItem>
                      <TableRowItem className="p-4">
                        <Action data={thisPredikatKinerjaOrganisasi} />
                      </TableRowItem>
                    </TableRow>
                  )
                )}
            </TableBody>
          </TablePelaksanaanKinerja>
          <Pagination
            pageSize={CURRENT_LIMIT}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalCount={dataPredikatKinerjaOrganisasi?.meta?.filteredTotal}
          />
        </section>
      </div>
    </div>
  )
}
export default KinerjaDinasPemberiPredikatKinerjaOrganisasiKirim

type DeliveryStatusProps = {
  value: TPredikatOrganisasiStatus
}

function DeliveryStatus({ value }: DeliveryStatusProps) {
  return (
    <Text
      className={clsx(
        'flex items-center gap-1.5',
        value === 'SENT' && '!text-icon-success',
        value === 'DINAS_UNCONFIRMED' && '!text-red-60'
      )}
    >
      {value === 'SENT' && <Icon color="inherit">check</Icon>}
      {PREDIKAT_ORGANISASI_STATUS_LABELS[value]}
    </Text>
  )
}

type ActionProps = {
  data: IPredikatKinerjaOrganisasi
}

function Action({ data }: ActionProps) {
  switch (data.deliveryStatus) {
    case 'READY_TO_SEND':
    case 'SENT':
    case 'NOT_SENT':
      return (
        <ActionUbahPredikat
          id={data.tahapPredikatId}
          initialValue={data.predicateRecommendation}
          deliveryStatus={data.deliveryStatus}
          submissionStep={data.submissionStep}
          schoolName={data.schoolName}
          npsn={data.npsn}
        />
      )
    case 'DINAS_UNCONFIRMED':
      return <ActionUnconfirmedInfo />
    case 'DINAS_REJECTED':
      return <ActionRejectedInfo />
    default:
      return null
  }
}

type ActionUbahPredikatProps = {
  deliveryStatus: TPredikatOrganisasiStatus
  id: string
  initialValue: TPredicateRecommendationResult
  schoolName: string
  submissionStep: TPredikatKinerjaSubmissionStep
  npsn: string
}
function ActionUbahPredikat({
  deliveryStatus,
  id,
  initialValue,
  schoolName,
  submissionStep,
  npsn,
}: ActionUbahPredikatProps) {
  const [isOpen, setOpen] = useState(false)
  const toast = useToast()

  const isEnabled =
    deliveryStatus === 'SENT' || deliveryStatus === 'READY_TO_SEND'

  const currentStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS[submissionStep]?.order ?? -1
  const sentToGuruStepOrder =
    PENETAPAN_PREDIKAT_KINERJA_STEPS['SUBMIT_GURU']?.order ?? Infinity
  const isSentToGuru = currentStepOrder > sentToGuruStepOrder

  const handleClick = () => {
    if (isSentToGuru) {
      toast({
        message:
          'Tidak dapat diubah lagi karena Kepala Sekolah satuan pendidikan sudah mengirim Predikat Kinerja Pegawai ke guru.',
      })
    } else if (isEnabled) {
      setOpen(true)
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        disabled={!isEnabled}
        aria-disabled={!isEnabled || isSentToGuru}
        className="text-left whitespace-nowrap"
        onClick={handleClick}
      >
        Ubah predikat
      </Button>

      <DialogUbahPredikatOrganisasi
        isOpen={isOpen}
        isSent={deliveryStatus === 'SENT'}
        id={id}
        npsn={npsn}
        schoolName={schoolName}
        initialValue={initialValue}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

function ActionUnconfirmedInfo() {
  const [isOpen, setOpen] = useState(false)

  return (
    <>
      <Link
        as="button"
        type="button"
        className="ml-1"
        onClick={() => setOpen(true)}
        tabIndex={0}
        underline
        variant="primary"
      >
        Info
      </Link>
      <Dialog isOpen={isOpen} size="md" onClose={() => setOpen(false)}>
        <DialogOverlay />
        <DialogContent>
          <div className="p-6 space-y-4">
            <div className="space-y-3">
              <Text variant="display-sm" as="h2" className="mb-3">
                Kabari Kepala Sekolah untuk konfirmasi data atasan
              </Text>

              <Text variant="body-lg" color="subdued">
                Predikat Kinerja Organisasi dapat dikirim setelah Kepala Sekolah
                konfirmasi data atasan melalui halaman:
              </Text>
              <img
                className="w-full"
                src={assetUrl(
                  '/images/pengelolaan-kinerja/banner-konfirmasi-atasan-ks.png'
                )}
                alt=""
                width={636}
                height={221}
              />
            </div>

            <Button variant="ghost" size="lg" onClick={() => setOpen(false)}>
              Tutup
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function ActionRejectedInfo() {
  const [isOpen, setOpen] = useState(false)
  const { profilePs } = useRoleContext()

  return (
    <>
      <Link
        as="button"
        type="button"
        className="ml-1"
        onClick={() => setOpen(true)}
        tabIndex={0}
        underline
        variant="primary"
      >
        Info
      </Link>
      <Dialog isOpen={isOpen} size="sm" onClose={() => setOpen(false)}>
        <DialogOverlay />
        <DialogContent>
          <div className="p-6 space-y-4">
            <img
              src={assetUrl('/images/errors/img-forbiden-access.png')}
              alt=""
              width={120}
              height={120}
              className="mb-4"
            />

            <div className="space-y-3">
              <Text variant="display-sm" as="h2" className="mb-3">
                {`Kepala Sekolah sudah konfirmasi bahwa ${profilePs.namaWilayah} tidak lagi menjadi Daerah Dinas Pendidikannya`}
              </Text>

              <Text variant="body-lg" color="subdued">
                Saat ini Anda tidak lagi memiliki akses terhadap Pengelolaan
                Kinerja pegawai. Pastikan data penunjukkan
                <span className="italic"> (plotting)</span> Tim Kerja di
                Simtendik sesuai.
              </Text>
            </div>

            <Button variant="ghost" size="lg" onClick={() => setOpen(false)}>
              Tutup
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
